import { DocumentDuplicateIcon } from "@heroicons/react/outline";
import { ControlledMenuProps, MenuItem } from "@szhsin/react-menu";
import { Menu } from "@szhsin/react-menu";
import { Editor } from "@tiptap/core";
import { noop } from "lodash";
import { Fragment } from "react";
import {
  TopicDataFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { parseStringToJSON } from "@helpers/helpers";

import AddToSubMenu from "./components/add-to-meetings";
import DeleteMenuItem from "./components/delete";
import DuplicateToCurrentMeetingMenuItem from "./components/duplicate-to-current-meeting";
import TogglePersonalNotesMenuItem from "./components/toggle-personal-notes";
import ToggleSharedNotesMenuItem from "./components/toggle-shared-notes";

const TopicDropdownMenu = ({
  topic,
  meeting,
  meetingGroup,
  className = "",
  currentPageMeeting = null,
  isLastTopic = false,
  onDelete = noop,
  size = ButtonSize.large,
}: ControlledMenuProps & {
  className?: string;
  topic: TopicNodeNewPageFragmentFragment | TopicDataFragmentFragment;
  meeting?: any;
  meetingGroup?: any;
  currentPageMeeting?: any;
  onToggleMenu?: any;
  isInSidebar?: boolean;
  isLastTopic?: boolean;
  onDelete?: any;
  size?: ButtonSize;
}) => {
  const canEditTopic = topic.canUpdate.permission && !topic.isMandatory;

  const previousNotes =
    topic.previousTopic &&
    !isEmptyValue(parseStringToJSON(topic.previousTopic.discussionNotes))
      ? parseStringToJSON(topic.previousTopic.discussionNotes)
      : null;

  const handleClickCopyPreviousNotes = () => {
    const el = document.getElementById(`editor-topic-${topic.id}`);
    if (el && previousNotes) {
      const editorContainer = el.querySelector(
        '[aria-label="Meeting topic shared notes"] .js-topic-discussion-notes-input',
      ) as Element & { editor?: Editor };
      if (
        previousNotes &&
        editorContainer.editor &&
        (editorContainer.editor.isEmpty ||
          (!editorContainer.editor.isEmpty &&
            window.confirm(
              "Confirm you want to replace your current notes with the previous meeting notes?",
            )))
      ) {
        editorContainer.editor.chain().setContent(previousNotes).run();
      }
    }
  };

  const sections = [
    topic.includesSharedNotes && previousNotes && (
      <MenuItem className="text-sm" onClick={handleClickCopyPreviousNotes}>
        <DocumentDuplicateIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
        Copy notes from previous meeting
      </MenuItem>
    ),
    canEditTopic && (
      <>
        {meetingGroup?.isFormalOneonone ? (
          <ToggleSharedNotesMenuItem topic={topic} meeting={meeting} />
        ) : (
          <TogglePersonalNotesMenuItem topic={topic} meeting={meeting} />
        )}
      </>
    ),
    meetingGroup &&
      meeting &&
      meeting.isRecurring &&
      currentPageMeeting &&
      meeting.id === currentPageMeeting.id && (
        <AddToSubMenu topic={topic} meetingGroup={meetingGroup} />
      ),
    meetingGroup &&
      meeting &&
      currentPageMeeting &&
      meeting.id !== currentPageMeeting.id && (
        <DuplicateToCurrentMeetingMenuItem
          topic={topic}
          meeting={meeting}
          meetingGroup={meetingGroup}
          currentPageMeeting={currentPageMeeting}
        />
      ),
    topic.canDelete.permission && !topic.isMandatory && (
      <DeleteMenuItem
        topic={topic}
        meeting={meeting}
        onDelete={onDelete}
        disabled={isLastTopic}
      />
    ),
  ].filter(Boolean);

  const items = (
    <>
      {sections.map((section, i) => (
        <Fragment key={i}>{section}</Fragment>
      ))}
    </>
  );

  return (
    <Menu
      portal
      className="z-dropdown"
      align="end"
      transition={false}
      aria-label="Topic dropdown menu list"
      menuButton={
        <Button
          icon={getTopicflowIcon("horizontalDots")}
          theme={buttonTheme.iconGray}
          size={size}
          className={className}
        />
      }
    >
      {items}
    </Menu>
  );
};
export default TopicDropdownMenu;
